import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AuroraMaxPressureLimitControl from './AuroraMaxPressureLimitControl'
import { AuroraAssetGroup, AuroraSetMaxPressureLimitActionInput } from '../types'
import { useAuroraStack } from '../hooks/aurora'
import { publishAuroraAwhpAction } from '../services/aurora'
import { createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupMaxPressureLimitControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (input: AuroraSetMaxPressureLimitActionInput) => void
  afterSubmit?: (value: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupMaxPressureLimitControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupMaxPressureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    maxPressureLimit: initialMaxPressureLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [maxMaxPressureLimit, setMaxPressureLimit] = useState<number | null>(
    initialMaxPressureLimit
  )

  async function handleSubmit(input: AuroraSetMaxPressureLimitActionInput) {
    try {
      setSubmitting(true)

      beforeSubmit?.(input)

      await Promise.all(auroras.map((aurora) => publishAuroraAwhpAction(aurora.id, input)))

      dispatch(createSuccessNotification(t('setMaxPressureLimitSuccess')))
      setMaxPressureLimit(input.alarms.maxPressLimit)
      afterSubmit?.(input.alarms.maxPressLimit)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setMaxPressureLimitError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraMaxPressureLimitControl
      value={maxMaxPressureLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
